/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module modals/AppointmentRescheduleModal/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import React from "react";
import { useTranslation } from "react-i18next";
import { Backdrop, CircularProgress } from "@mui/material";
import styles from "./index.module.css";

import { ReactComponent as ArrowIcon } from "../../assets/icons/AppointmentRescheduleModal/ArrowIcon.svg";
import { ReactComponent as CloseIcon } from "../../assets/icons/AppointmentRescheduleModal/CloseIcon.svg";

const AppointmentRescheduleModal = ({
  open,
  onClose,
  onConfirm,
  appointment,
  newDate,
  newTime,
  eventData,
  isLoading = false,
}) => {
  const { t, i18n } = useTranslation();

  if (!open || !appointment) {
    return null;
  }

  const formatDate = (date) => {
    const weekdays = {
      en: [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
      ],
      es: [
        "Domingo",
        "Lunes",
        "Martes",
        "Miércoles",
        "Jueves",
        "Viernes",
        "Sábado",
      ],
      fr: [
        "Dimanche",
        "Lundi",
        "Mardi",
        "Mercredi",
        "Jeudi",
        "Vendredi",
        "Samedi",
      ],
    };

    const months = {
      en: [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ],
      es: [
        "Ene",
        "Feb",
        "Mar",
        "Abr",
        "May",
        "Jun",
        "Jul",
        "Ago",
        "Sep",
        "Oct",
        "Nov",
        "Dic",
      ],
      fr: [
        "Jan",
        "Fév",
        "Mar",
        "Avr",
        "Mai",
        "Jun",
        "Jul",
        "Aoû",
        "Sep",
        "Oct",
        "Nov",
        "Déc",
      ],
    };

    const language = i18n.language || "es";
    const weekday =
      weekdays[language]?.[date.getDay()] || weekdays.es[date.getDay()];
    const month =
      months[language]?.[date.getMonth()] || months.es[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();

    return `${weekday} ${day}, ${month} ${year}`;
  };

  const formatTime = (time) => {
    const hours = time.getHours();
    const minutes = time.getMinutes();
    const formattedTime = `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}`;

    // Add PM suffix for afternoon hours (12:00 and later)
    if (hours >= 12) {
      return `${formattedTime} PM`;
    } else {
      return `${formattedTime} AM`;
    }
  };

  const handleConfirm = async () => {
    if (onConfirm) {
      // Pass the reschedule data to the onConfirm callback
      const rescheduleData = {
        appointment,
        newDate,
        newTime,
      };
      await onConfirm(rescheduleData);
    }
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  const oldDate = new Date(appointment.fecha + "T00:00:00");
  const oldStartTime = new Date(appointment.fecha + "T00:00:00");
  const [oldHour, oldMinute] = appointment.horaInicio.split(":").map(Number);
  oldStartTime.setHours(oldHour, oldMinute, 0, 0);

  const oldEndTime = new Date(appointment.fecha + "T00:00:00");
  const [oldEndHour, oldEndMinute] = appointment.horaFin.split(":").map(Number);
  oldEndTime.setHours(oldEndHour, oldEndMinute, 0, 0);

  const newStartTime = new Date(newDate);
  const [newHour, newMinute] = newTime.split(":").map(Number);
  newStartTime.setHours(newHour, newMinute, 0, 0);

  const newEndTime = new Date(newDate);

  // Calculate duration based on event data instead of original appointment
  let durationMinutes = 60; // Default to 60 minutes (1 hour)

  if (eventData && eventData.duracionCitas) {
    // Parse event duration (format: "00:20", "01:30", etc.)
    const [hours, minutes] = eventData.duracionCitas.split(":").map(Number);
    durationMinutes = hours * 60 + minutes;
  }

  const duration = durationMinutes * 60 * 1000; // Convert to milliseconds
  newEndTime.setTime(newStartTime.getTime() + duration);

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>
            {t("components.appointmentReschedule.title")}
          </h3>
          <button
            className={styles.closeButton}
            onClick={handleCancel}
            disabled={isLoading}
          >
            <CloseIcon />
          </button>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.modalSubtitle}>
            {t("components.appointmentReschedule.subtitle", {
              memberName: appointment.memberName,
            })}
          </div>

          <div className={styles.scheduleChange}>
            <div className={styles.scheduleSection}>
              <div className={styles.dateTime}>
                <span className={styles.date}>{formatDate(oldDate)}</span>
                <span className={styles.time}>
                  {formatTime(oldStartTime)} - {formatTime(oldEndTime)}
                </span>
              </div>
            </div>

            <div className={styles.arrow}>
              <ArrowIcon />
            </div>

            <div className={styles.scheduleSection}>
              <div className={styles.dateTime}>
                <span className={styles.date}>{formatDate(newDate)}</span>
                <span className={styles.time}>
                  {formatTime(newStartTime)} - {formatTime(newEndTime)}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.modalActions}>
          <button
            className={`${styles.modalButton} ${styles.cancelButton}`}
            onClick={handleCancel}
            disabled={isLoading}
          >
            {t("components.appointmentReschedule.cancel")}
          </button>
          <button
            className={`${styles.modalButton} ${styles.confirmButton}`}
            onClick={handleConfirm}
            disabled={isLoading}
          >
            {isLoading ? (
              <span className={styles.loadingText}>
                {t("components.appointmentReschedule.rescheduling")}
              </span>
            ) : (
              t("components.appointmentReschedule.confirm")
            )}
          </button>
        </div>
      </div>

      {/* Loading Backdrop */}
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(4px)",
        }}
        open={isLoading}
      >
        <CircularProgress
          size={60}
          thickness={4}
          sx={{
            color: "#4eabe7",
          }}
        />
      </Backdrop>
    </div>
  );
};

export default AppointmentRescheduleModal;
